import { MILL_2 } from "@/setup/mill-config/mill-config";
import { FeetInchesInput } from "@/shared/components/FeetInchesInput";
import { HeatSelector } from "@/shared/selectors/HeatSelector";
import { SectionSelector } from "@/shared/selectors/SectionSelector";
import { Box, Button, TextField, Typography } from "@mui/material";
import { KeyboardEvent, useState } from "react";
import { BundleDetail, BundleValues } from "../../models/FinishedBundle";

const common = <T,>(rows: BundleDetail[], pick: (b: BundleDetail) => T) =>
	rows.length && rows.every((b) => pick(b) === pick(rows[0]))
		? pick(rows[0])
		: null;

const toInches = (ft: number | null) =>
	ft === null ? null : Math.round(ft * 12);

type Props = {
	bundles: BundleDetail[];
	disabled: boolean;
	shiftId?: number;
	shiftHeatIds: number[];
	suggestedHeatIds?: number[];
	onSave: (values: BundleValues & { quantity?: number }) => void;
	onCancel: () => void;
};

export const BundleForm = ({
	bundles,
	disabled,
	shiftId,
	shiftHeatIds,
	suggestedHeatIds,
	onSave,
	onCancel,
}: Props) => {
	const isCreate = bundles.length === 0;
	const start = {
		heatId: common(bundles, (b) => b.HeatId),
		sectionId: common(bundles, (b) => b.SectionId),
		lengthInches: toInches(common(bundles, (b) => b.LengthFt)),
		pieces: String(common(bundles, (b) => b.Pieces) ?? ""),
		quantity: "1",
	};

	const [form, setForm] = useState(start);

	const lengthFt =
		form.lengthInches === null
			? null
			: Math.round((form.lengthInches / 12) * 100) / 100;
	const pieces = form.pieces === "" ? null : Number(form.pieces);
	const quantity = Number(form.quantity);
	const values: BundleValues = isCreate
		? {
				heatId: form.heatId,
				sectionId: form.sectionId,
				lengthFt,
				pieces,
			}
		: {
				heatId: form.heatId !== start.heatId ? form.heatId : null,
				sectionId: form.sectionId !== start.sectionId ? form.sectionId : null,
				lengthFt: form.lengthInches !== start.lengthInches ? lengthFt : null,
				pieces: form.pieces !== start.pieces ? pieces : null,
			};

	const invalid =
		(lengthFt !== null && !(lengthFt > 0)) ||
		(pieces !== null && !(pieces >= 0 && Number.isInteger(pieces))) ||
		(isCreate &&
			(!form.heatId ||
				!form.sectionId ||
				lengthFt === null ||
				pieces === null ||
				!(quantity >= 1 && quantity <= 500 && Number.isInteger(quantity))));
	const nothingChanged =
		!isCreate && Object.values(values).every((v) => v === null);
	const canSave = !invalid && !nothingChanged && !disabled;

	const save = () => {
		if (canSave) onSave(isCreate ? { ...values, quantity } : values);
	};
	const onKeyDown = (e: KeyboardEvent) => {
		if (e.key === "Enter") save();
		if (e.key === "Escape") onCancel();
	};

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				py: 1,
				flexWrap: "wrap",
				"& .MuiInputBase-root": { backgroundColor: "white" },
			}}
			onClick={(e) => e.stopPropagation()}
			data-testid={isCreate ? "bundle-create-form" : "bundle-edit-form"}
		>
			<Box sx={{ width: 150 }}>
				<SectionSelector
					label="Section"
					value={form.sectionId}
					onChange={(value) => setForm({ ...form, sectionId: value })}
					size="small"
					sortBy="recent"
					hideTph
					assetId={MILL_2.id}
					shiftId={shiftId}
				/>
			</Box>
			<Box sx={{ width: 140 }}>
				<HeatSelector
					value={form.heatId}
					onChange={(heatId) => setForm({ ...form, heatId })}
					sectionId={form.sectionId ?? undefined}
					assetId={MILL_2.id}
					priorityIds={shiftHeatIds}
					suggestedIds={suggestedHeatIds}
				/>
			</Box>
			<Box
				sx={{ width: 130 }}
				onKeyDown={onKeyDown}
				data-testid="bundle-length-input"
			>
				<FeetInchesInput
					label="Length"
					value={form.lengthInches}
					onChange={(lengthInches) => setForm({ ...form, lengthInches })}
				/>
			</Box>
			{isCreate && (
				<>
					<TextField
						size="small"
						type="number"
						label="Bundles"
						value={form.quantity}
						onChange={(e) => setForm({ ...form, quantity: e.target.value })}
						onKeyDown={onKeyDown}
						inputProps={{
							min: 1,
							max: 500,
							"data-testid": "bundle-quantity-input",
						}}
						sx={{
							width: 72,
							"& .MuiInputBase-input": {
								textAlign: "right",
								MozAppearance: "textfield",
							},
							"& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
								{
									WebkitAppearance: "none",
									margin: 0,
								},
						}}
					/>
					<Typography color="text.secondary">×</Typography>
				</>
			)}
			<TextField
				size="small"
				type="number"
				label="Pieces"
				value={form.pieces}
				onChange={(e) => setForm({ ...form, pieces: e.target.value })}
				onKeyDown={onKeyDown}
				inputProps={{ min: 0, "data-testid": "bundle-pieces-input" }}
				sx={{
					width: 72,
					"& .MuiInputBase-input": {
						textAlign: "right",
						MozAppearance: "textfield",
					},
					"& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
						{
							WebkitAppearance: "none",
							margin: 0,
						},
				}}
			/>
			<Box sx={{ display: "flex", gap: 0.5, ml: "auto" }}>
				<Button size="small" onClick={onCancel}>
					Cancel
				</Button>
				<Button
					size="small"
					variant="contained"
					disabled={!canSave}
					onClick={save}
					data-testid="bundle-form-save"
				>
					{isCreate ? `Add ${quantity || 0} × ${form.pieces || 0}` : "Apply"}
				</Button>
			</Box>
		</Box>
	);
};
